import { MailIcon, SignatureIcon, Text } from '@viapeople/components'
import type { ReactElement } from 'react'
import { useCycleStepsStore } from '../CycleSteps/store/useCycleStepsStore'

interface OptionConfig {
	key: 'hierarchyLevels' | 'blacklistedHierarchyRoles' | 'roles' | 'whitelistedUsers' | 'blacklistedUsers'
	label: string
	mapFn: (item: { label: string } | number) => string
}

type OptionGroup = {
	hierarchyLevels?: number[]
	blacklistedHierarchyRoles?: Array<{ id: string; label: string }>
	roles?: Array<{ id: string; label: string }>
	whitelistedUsers?: Array<{ id: string; label: string }>
	blacklistedUsers?: Array<{ id: string; label: string }>
}

const optionConfigs: OptionConfig[] = [
	{
		key: 'hierarchyLevels',
		label: 'Hierarchy Levels',
		mapFn: (item: { label: string } | number) => (typeof item === 'number' ? item.toString() : item.label),
	},
	{
		key: 'blacklistedHierarchyRoles',
		label: 'Blacklisted Hierarchy Roles',
		mapFn: (item: { label: string } | number) => (typeof item === 'number' ? item.toString() : item.label),
	},
	{
		key: 'roles',
		label: 'Form Roles',
		mapFn: (item: { label: string } | number) => (typeof item === 'number' ? item.toString() : item.label),
	},
	{
		key: 'whitelistedUsers',
		label: 'Whitelisted Users',
		mapFn: (item: { label: string } | number) => (typeof item === 'number' ? item.toString() : item.label),
	},
	{
		key: 'blacklistedUsers',
		label: 'Blacklisted Users',
		mapFn: (item: { label: string } | number) => (typeof item === 'number' ? item.toString() : item.label),
	},
]

const renderOptionGroup = (group: OptionGroup | undefined) => {
	if (!group) return null

	return optionConfigs.map(
		({ key, label, mapFn }) =>
			group[key] &&
			group[key].length > 0 && (
				<div key={key}>
					<Text className='text-left' variant='hint' weight='semibold'>
						{label}
					</Text>
					<Text className='text-left' variant='hint' weight='regular'>
						{group[key].map(mapFn).join(', ')}
					</Text>
				</div>
			),
	)
}

interface StepInfoProps {
	stepId: string
}

type StepOptions = {
	participants?: OptionGroup
	releasers?: OptionGroup
	email?: {
		kickoffTemplateId?: string
		reminderTemplateId?: string
		lastCallTemplateId?: string
		pastDueTemplateId?: string
	}
	requireSignature?: boolean
}

export function StepInfo({ stepId }: StepInfoProps): ReactElement | null {
	const { nodes } = useCycleStepsStore()
	const node = nodes.find((n) => n.id === stepId)

	if (!node) return null

	const options = node.data.options as StepOptions

	const hasParticipants =
		options?.participants &&
		((options?.participants?.roles && options?.participants?.roles?.length > 0) ||
			(options?.participants?.whitelistedUsers && options?.participants?.whitelistedUsers?.length > 0) ||
			(options?.participants?.blacklistedUsers && options?.participants?.blacklistedUsers?.length > 0) ||
			(options?.participants?.blacklistedHierarchyRoles &&
				options?.participants?.blacklistedHierarchyRoles?.length > 0))

	const hasReleasers =
		options?.releasers &&
		((options?.releasers?.roles && options?.releasers?.roles?.length > 0) ||
			(options?.releasers?.whitelistedUsers && options?.releasers?.whitelistedUsers?.length > 0) ||
			(options?.releasers?.blacklistedUsers && options?.releasers?.blacklistedUsers?.length > 0) ||
			(options?.releasers?.blacklistedHierarchyRoles && options?.releasers?.blacklistedHierarchyRoles?.length > 0))

	return (
		<div className='absolute top-20 right-4 w-[224px] bg-white rounded-lg shadow-lg z-[1000] p-4'>
			<h2 className='text-md text-left font-semibold mb-2'>{node.data.label}</h2>

			{options && (
				<div>
					<div className='flex flex-col mt-2 gap-3 text-sm'>
						{hasParticipants && (
							<div>
								<Text className='text-left' variant='xsmall' weight='semibold'>
									Participants
								</Text>
							</div>
						)}
						{renderOptionGroup(options?.participants)}

						{hasReleasers && (
							<div>
								<Text className='text-left' variant='xsmall' weight='semibold'>
									Releasers
								</Text>
							</div>
						)}
						{renderOptionGroup(options?.releasers)}

						{(() => {
							const activeEmails = [
								options?.email?.kickoffTemplateId ? '1 kickoff' : null,
								options?.email?.reminderTemplateId ? '1 reminder' : null,
								options?.email?.lastCallTemplateId ? '1 last call' : null,
								options?.email?.pastDueTemplateId ? '1 past due' : null,
							].filter(Boolean)

							if (activeEmails.length === 0) return null

							return (
								<div className='flex gap-2'>
									<MailIcon className='fill-neutral-500' />
									<Text variant='xsmall' className='text-left'>
										{activeEmails.join(', ')} email{activeEmails.length > 1 ? 's' : ''}
									</Text>
								</div>
							)
						})()}

						{options?.requireSignature && (
							<div className='flex gap-2'>
								<SignatureIcon className='fill-neutral-500' />
								<Text variant='xsmall' className='text-left'>
									Signature required
								</Text>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
